import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SendIcon from '@mui/icons-material/Send';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Divider, Grid, IconButton, Skeleton, Stack, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { MessageUsersOnlineWelcomeMessage } from '../../../../hooks/useMessageUsersAutoWelcomeMessageOnlineUsers';
import useMessageUsersAutoWelcomeMessageOnlineUsersWelcomeMessageStats from '../../../../hooks/useMessageUsersAutoWelcomeMessageOnlineUsersWelcomeMessageStats';
import MessageUsersFormEditiorPreview from '../sendMessage/MessageUsersFormEditiorPreview';

const AutoSendWelcomeMessageOnlineFansWelcomeMessageRow = ({
    welcomeMessage,
    index,
    isUpdatingAutoWelcomeMessageOnlineUsers,
    isDeletingAutoWelcomeMessageOnlineUsers,
    toggleActive,
    editMessage,
    deleteMessage,
}: {
    welcomeMessage: MessageUsersOnlineWelcomeMessage;
    index: number;
    isUpdatingAutoWelcomeMessageOnlineUsers: boolean;
    isDeletingAutoWelcomeMessageOnlineUsers: boolean;
    toggleActive: (message: MessageUsersOnlineWelcomeMessage) => void;
    editMessage: (message: MessageUsersOnlineWelcomeMessage) => void;
    deleteMessage: (message: MessageUsersOnlineWelcomeMessage) => void;
}) => {
    const dialog = useDialog();
    const theme: Theme = useTheme();

    const { data: stats, isLoading: isStatsLoading } = useMessageUsersAutoWelcomeMessageOnlineUsersWelcomeMessageStats(welcomeMessage._id);

    return (
        <Grid item xs={12} key={welcomeMessage._id}>
            {index > 0 && <Divider sx={{ mb: 1 }} />}
            <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                {/** Title */}
                <Grid item xs>
                    <Typography
                        variant="inherit"
                        fontWeight="600"
                        color="textPrimary"
                        noWrap
                        sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                        }}
                    >
                        {welcomeMessage.name}
                    </Typography>
                </Grid>

                {/** Actions */}
                <Grid item xs="auto">
                    <Stack direction="row" spacing={0}>
                        <Tooltip title="Toggle On or Off This Message">
                            <IconButton
                                size="small"
                                disabled={isUpdatingAutoWelcomeMessageOnlineUsers || isDeletingAutoWelcomeMessageOnlineUsers}
                                color={welcomeMessage.messageDetails && welcomeMessage.messageDetails.active ? 'success' : 'error'}
                                onClick={() => toggleActive(welcomeMessage)}
                            >
                                <PowerSettingsNewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit This Message">
                            <IconButton
                                size="small"
                                color="warning"
                                disabled={isUpdatingAutoWelcomeMessageOnlineUsers || isDeletingAutoWelcomeMessageOnlineUsers}
                                onClick={() => editMessage(welcomeMessage)}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete This Message">
                            <IconButton
                                size="small"
                                color="error"
                                disabled={isUpdatingAutoWelcomeMessageOnlineUsers || isDeletingAutoWelcomeMessageOnlineUsers}
                                onClick={() => {
                                    dialog
                                        .confirm({
                                            message: 'Are you sure you want to delete this message?',
                                            ok: {
                                                text: 'Yes',
                                                variant: 'contained',
                                                color: 'error',
                                            },
                                            cancel: {
                                                text: 'No',
                                                variant: 'text',
                                                color: 'primary',
                                            },
                                        })
                                        .then(() => {
                                            deleteMessage(welcomeMessage);
                                        })
                                        .catch(() => {
                                            // do nothing on cancel
                                        });
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Grid>
            </Grid>

            {/* Stats Section */}
            <Grid container spacing={1} justifyContent="flex-start" alignItems="center" mb={0.5}>
                <Grid item xs="auto">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="PPV Price">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <AttachMoneyIcon
                                    htmlColor={
                                        welcomeMessage.messageDetails.lockMessagePrice
                                            ? theme.palette.success.main
                                            : theme.palette.text.disabled
                                    }
                                    sx={{ fontSize: '12pt' }}
                                />
                            </div>
                        </Tooltip>
                        <Typography
                            variant="body2"
                            color={welcomeMessage.messageDetails.lockMessagePrice ? 'textPrimary' : 'textSecondary'}
                            noWrap
                        >
                            {welcomeMessage.messageDetails.lockMessagePrice
                                ? `$${welcomeMessage.messageDetails.lockMessagePrice.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                  })}`
                                : 'FREE'}
                        </Typography>
                    </Stack>
                </Grid>
                <Grid item xs="auto">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Purchases">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <ShoppingCartIcon
                                    htmlColor={
                                        welcomeMessage.messageDetails.lockMessagePrice
                                            ? theme.palette.success.main
                                            : theme.palette.text.disabled
                                    }
                                    sx={{ fontSize: '12pt' }}
                                />
                            </div>
                        </Tooltip>
                        <Typography
                            variant="body2"
                            color={welcomeMessage.messageDetails.lockMessagePrice ? 'textPrimary' : 'textSecondary'}
                            noWrap
                        >
                            {isStatsLoading && !stats && <Skeleton variant="text" width={50} height={14} />}
                            {!isStatsLoading &&
                                stats &&
                                stats.purchasesTotal.toLocaleString &&
                                `$${stats.purchasesTotal.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })} (${stats ? stats.purchasesCount.toLocaleString() : '0'})`}
                        </Typography>
                    </Stack>
                </Grid>
                <Grid item xs="auto">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <ImageIcon
                            color={
                                welcomeMessage.messageDetails.mediaFiles && welcomeMessage.messageDetails.mediaFiles.length > 0
                                    ? 'primary'
                                    : 'disabled'
                            }
                            sx={{ fontSize: '12pt' }}
                        />
                        <Typography
                            variant="body2"
                            color={
                                welcomeMessage.messageDetails.mediaFiles && welcomeMessage.messageDetails.mediaFiles.length > 0
                                    ? 'textPrimary'
                                    : 'textSecondary'
                            }
                            noWrap
                        >
                            {welcomeMessage.messageDetails.mediaFiles && welcomeMessage.messageDetails.mediaFiles.length > 0
                                ? `${welcomeMessage.messageDetails.mediaFiles.length.toLocaleString()} ${
                                      welcomeMessage.messageDetails.mediaFiles.length > 1 ? 'attachment' : 'attachments'
                                  }`
                                : 'No Attachments'}
                        </Typography>
                    </Stack>
                </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="center" mb={1}>
                <Grid item xs="auto">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <RecordVoiceOverIcon
                            color={welcomeMessage.messageDetails.tts?.active ? 'primary' : 'disabled'}
                            sx={{ fontSize: '12pt' }}
                        />
                        <Typography
                            variant="body2"
                            color={
                                welcomeMessage.messageDetails.mediaFiles && welcomeMessage.messageDetails.mediaFiles.length > 0
                                    ? 'textPrimary'
                                    : 'textSecondary'
                            }
                            noWrap
                        >
                            {welcomeMessage.messageDetails.tts && welcomeMessage.messageDetails.tts.active
                                ? 'AI Voice Enabled'
                                : 'No AI Voice'}
                        </Typography>
                    </Stack>
                </Grid>
                <Grid item xs>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <SendIcon color={welcomeMessage.sentCount > 0 ? 'primary' : 'disabled'} sx={{ fontSize: '12pt' }} />
                        <Typography variant="body2" color={welcomeMessage.sentCount > 0 ? 'textPrimary' : 'textSecondary'} noWrap>
                            Sent: {welcomeMessage.sentCount !== undefined ? welcomeMessage.sentCount.toLocaleString() : '0'}
                        </Typography>
                    </Stack>
                </Grid>
            </Grid>
            <MessageUsersFormEditiorPreview
                message={welcomeMessage.messageDetails.message}
                lockMessage={!welcomeMessage.messageDetails.lockMessagePrice || welcomeMessage.messageDetails.lockMessagePrice === 0}
                lockMessagePrice={welcomeMessage.messageDetails.lockMessagePrice || 0}
                lockedText={welcomeMessage.messageDetails.lockedText || false}
                taggedCreators={welcomeMessage.messageDetails.taggedCreators}
                taggedReleaseForms={[]}
                theme={theme}
                fullWidth
            />
        </Grid>
    );
};

export default AutoSendWelcomeMessageOnlineFansWelcomeMessageRow;
